import {
  memo,
  useCallback,
  useContext,
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import styled from "@emotion/styled";
import { TradeDirection, TradeFlow, LocationDetailLevel } from "@atlas-int/data/types";
import { OverTimeLayoutOption, OverTimeOrderingOption, YAxisScalingOption, yAxisScalingLabels, CHART_MARGIN } from "../constants";
import { getColorMap } from "@atlas-int/viz";
import { getDataTradeDirectionSelector, TradeDirectionSelectorString, UIView } from "@atlas-int/data";
import { ChartContainerSizeContext } from "@atlas-int/viz/shared";
import { determineClosestXValue } from "@atlas-int/viz/shared";
import YAxisScalingOptionsSelector from "./YAxisScalingOptionsSelector";
import {
  YAxisExtentDomain,
  ChartQueryVariables,
  XAxisTooltipValue,
  SeriesDataPoint,
  MappedSeriesData,
} from "../types";
import type { YearRangeForProductClass } from "../types";
import type { MetadataFetchState, LocationMetadataPayload, LocationYearDatum, DeflatorDatum } from "@atlas-int/data/types";
import type { ProductMetadataBundle } from "@atlas-int/data/types";
import type { ExploreDataRecord } from "@atlas-int/data";
import {
  useChartScales,
  useStackAreaGenerator,
  useChartDataTransformation,
  useSeriesDataMapper,
  useDownloadData,
} from "../hooks";
import ChartSVG from "./ChartSVG";
import { useDownload } from "@atlas-int/viz";
import { dataViz, dataVizTypography, fontFamily } from "@atlas-int/tokens";

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0px;
  padding: 0px;

  & svg {
    margin: 0px;
    padding: 0px;
  }
`;

/**
 * SVG-based Y-axis label hidden in the browser (opacity 0) and revealed in
 * downloads via the data-download-only attribute (onclone sets opacity 1).
 * Positioned at the far left edge, before the tick labels.
 */
const DownloadYAxisLabelContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 100%;
  padding-bottom: ${CHART_MARGIN.BOTTOM}px;
  box-sizing: border-box;
  pointer-events: none;
  opacity: 0;
`;

interface ChartProps {
  inputData: { data: ExploreDataRecord[] };
  yearRangeForProductClass: YearRangeForProductClass;
  hiddenCategories: string[];
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  locationMetadata: MetadataFetchState<LocationMetadataPayload>;
  productMetadata: ProductMetadataBundle;
  locationYearMetadata: MetadataFetchState<LocationYearDatum[]>;
  deflatorsMetadata: MetadataFetchState<DeflatorDatum[]>;
  queryVariables: ChartQueryVariables;
  isLoading?: boolean;
  onShowTooltip: (data: {
    seriesKey: string;
    series: SeriesDataPoint[];
    fillColor: string;
    year?: number;
  }) => void;
  onHideTooltip: () => void;
  highlightedItem?: string;
  onSetSeriesData: (data: MappedSeriesData[]) => void;
  setXAxisTooltipValue: (value: XAxisTooltipValue) => void;
  overTimeLayout: OverTimeLayoutOption;
  overTimeOrdering: OverTimeOrderingOption;
  locationLevel: LocationDetailLevel;
  onYAxisChange?: (yAxis: string) => void;
}

const Chart = memo(
  ({
    inputData,
    yearRangeForProductClass,
    hiddenCategories,
    tradeDirection,
    tradeFlow,
    locationMetadata,
    productMetadata,
    locationYearMetadata,
    deflatorsMetadata,
    queryVariables,
    onShowTooltip,
    onHideTooltip,
    highlightedItem: _highlightedItem,
    onSetSeriesData,
    setXAxisTooltipValue,
    overTimeLayout,
    overTimeOrdering,
    locationLevel,
    onYAxisChange,
  }: ChartProps) => {
    const {
      view,
      productClass,
      productLevel,
      yearMin,
      yearMax,
      exporter,
      importer,
    } = queryVariables;

    // State management
    const [yAxisExtentDomain, setYAxisExtentDomain] = useState<
      YAxisExtentDomain | undefined
    >(undefined);

    const { svgRef, dataRef } = useDownload();
    const { chartWidth, chartHeight } = useContext(ChartContainerSizeContext);

    // Computed values
    // The tradeDirectionSelector determines which data field (exportValue or importValue) to use.
    // Uses centralized getDataTradeDirectionSelector which handles World→World swap for Markets view.
    const tradeDirectionSelector = useMemo((): TradeDirectionSelectorString => {
      return getDataTradeDirectionSelector({
        tradeDirection,
        exporter,
        importer,
        view: view as UIView,
      }) as TradeDirectionSelectorString;
    }, [tradeDirection, exporter, importer, view]);

    const useColorMap: Map<string, string> = useMemo(
      () => getColorMap({ view, productClass }),
      [view, productClass],
    );

    const setYAxisDomain = useCallback(
      (domainToSet: { min: number; max: number }) => {
        if (
          !yAxisExtentDomain ||
          (yAxisExtentDomain &&
            (domainToSet.min !== yAxisExtentDomain.min ||
              domainToSet.max !== yAxisExtentDomain.max))
        ) {
          setYAxisExtentDomain({ min: domainToSet.min, max: domainToSet.max });
        }
      },
      [yAxisExtentDomain],
    );

    // Data transformation
    // Note: totalSum and findInVizOptions are now derived in the OvertimeDataContext
    // This hook is still called here for the chart-specific data (seriesData, scaledTradeValueSelector)
    const { seriesData, noDataToDisplay, scaledTradeValueSelector } =
      useChartDataTransformation({
        inputData,
        queryVariables,
        yearRangeForProductClass,
        hiddenCategories,
        overTimeLayout,
        overTimeOrdering,
        locationLevel,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
        locationMetadata,
        productMetadata,
        locationYearMetadata,
        deflatorsMetadata,
        setYAxisDomain,
      });

    // Chart scales
    const { xScale, yScale, chartMargin } = useChartScales({
      yearMin,
      yearMax,
      chartWidth,
      chartHeight,
      overTimeLayout,
      yAxisExtentDomain,
    });

    // Stack area generator
    const stackArea = useStackAreaGenerator(xScale, yScale);

    // Map series data
    const mappedSeriesData = useSeriesDataMapper({
      seriesData,
      view,
      locationLevel,
      productLevel,
      scaledTradeValueSelector,
      overTimeLayout,
      yScale,
    });

    // Container size
    const containerSize = useMemo(
      () => ({ width: chartWidth, height: chartHeight }),
      [chartWidth, chartHeight],
    );

    // Chart mouse move handler - use ref to keep stable
    const setXAxisTooltipValueRef = useRef(setXAxisTooltipValue);
    setXAxisTooltipValueRef.current = setXAxisTooltipValue;

    const handleChartMouseMove = useCallback(
      (e: React.MouseEvent<SVGElement>) => {
        determineClosestXValue({
          e,
          xScale,
          containerSize: { width: chartWidth, height: chartHeight },
          chartMargin,
          setXAxisTooltipValue: (value) => {
            setXAxisTooltipValueRef.current({
              ...value,
              xScale,
              yScale,
            });
          },
        });
      },
      [xScale, yScale, chartWidth, chartHeight, chartMargin],
    );

    // Download data
    useDownloadData({
      seriesData,
      tradeDirectionSelector,
      yAxisScalingOption: queryVariables.yAxis,
      dataRef,
    });

    // Share series data with parent for highlighted item tooltip (call only when data changes)
    useEffect(() => {
      if (mappedSeriesData) {
        onSetSeriesData(mappedSeriesData);
      }
    }, [mappedSeriesData, onSetSeriesData]);

    // Render logic — loading/nodata overlays are handled by the parent
    // (ChartWithTooltip), so we only return null here to avoid duplicates.
    const hasNoData =
      (seriesData === undefined && noDataToDisplay === true) ||
      seriesData?.length === 0;

    if (!seriesData || hasNoData) {
      return null;
    }

    const yAxisLabel = queryVariables.yAxis === YAxisScalingOption.Current
      ? `Current ${tradeFlow} ${tradeDirection}`
      : yAxisScalingLabels[queryVariables.yAxis as YAxisScalingOption] ?? queryVariables.yAxis;

    return (
      <Root>
        <DownloadYAxisLabelContainer data-download-only>
          <svg viewBox="0 0 20 200" width="20" height="100%" preserveAspectRatio="xMidYMid meet">
            <text
              x="10"
              y="100"
              textAnchor="middle"
              dominantBaseline="central"
              transform="rotate(-90, 10, 100)"
              style={{
                fontSize: "14px",
                fontFamily: `'${fontFamily.base}', sans-serif`,
                fill: dataViz.axisText,
              }}
            >
              {yAxisLabel}
            </text>
          </svg>
        </DownloadYAxisLabelContainer>
        <YAxisScalingOptionsSelector
          view={view}
          tradeFlow={tradeFlow}
          yAxisScalingOptionsSelectorMargin={CHART_MARGIN.Y_AXIS_SELECTOR_WIDTH}
          yAxisScalingOption={queryVariables.yAxis}
          tradeDirection={tradeDirection as unknown as string}
          onYAxisChange={onYAxisChange || (() => {})}
        />
        <ChartSVG
          chartWidth={chartWidth}
          chartHeight={chartHeight}
          handleChartMouseMove={handleChartMouseMove}
          clearTooltip={onHideTooltip}
          svgRef={svgRef}
          yScale={yScale}
          xScale={xScale}
          containerSize={containerSize}
          chartMargin={chartMargin}
          mappedSeriesData={mappedSeriesData}
          stackArea={stackArea}
          useColorMap={useColorMap}
          onRibbonMouseEnter={onShowTooltip}
          onRibbonMouseLeave={onHideTooltip}
          overTimeLayout={overTimeLayout}
        />
      </Root>
    );
  },
);

export default Chart;
